import React, { useEffect, useRef, useState } from "react";
import { Camera, Video, Upload, Loader2, X } from "lucide-react";

// Photo / video attached to an AI Menu item (AIMenuScreen item card).
// The button opens a small menu: Take photo, Record video, Upload from device.
//  - Phones/tablets: "Take photo" / "Record video" open the device camera app (<input capture>).
//  - Computers: they open a live camera pop-up (getUserMedia / MediaRecorder); if the browser has no
//    camera API (e.g. plain http) they fall back to the file picker.
// The chosen file goes to onFile(file); validation errors go to onError(message).

export const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
export const VIDEO_TYPES = ["video/mp4", "video/webm", "video/quicktime"];
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const MAX_VIDEO_BYTES = 250 * 1024 * 1024; // larger than 50 MB is compressed to under 50 MB by the server
const MAX_RECORD_SECONDS = 60;

export const isVideoUrl = (url) => /\.(mp4|webm|mov)(?:[?#].*)?$/i.test(String(url || ""));

const isTouchDevice = () =>
  typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
const hasCameraApi = () => !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);

export default function ItemMediaUpload({ itemName, uploading, disabled = false, hasMedia, onFile, onError }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [camera, setCamera] = useState(null); // null | "photo" | "video"
  const [cameraError, setCameraError] = useState("");
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);

  const pickerRef = useRef(null); // any image / video from the device
  const photoCaptureRef = useRef(null); // phone camera: photo
  const videoCaptureRef = useRef(null); // phone camera: video
  const previewRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const deliver = (file) => {
    if (!file) return;
    const isImage = IMAGE_TYPES.includes(file.type);
    const isVideo = VIDEO_TYPES.includes(file.type);
    if (!isImage && !isVideo) return onError("Please choose a JPG, PNG, WebP or GIF image, or an MP4, WebM or MOV video.");
    if (isImage && file.size > MAX_IMAGE_BYTES) return onError("Images must be 8 MB or smaller.");
    if (isVideo && file.size > MAX_VIDEO_BYTES) return onError("Videos must be 250 MB or smaller.");
    onFile(file);
  };

  const onInputChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // lets the same file be chosen again
    deliver(file);
  };

  const stopCamera = () => {
    clearInterval(timerRef.current);
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.onstop = null;
      try { recorderRef.current.stop(); } catch { /* already stopped */ }
    }
    recorderRef.current = null;
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setRecording(false);
    setSeconds(0);
  };

  const closeCamera = () => {
    stopCamera();
    setCamera(null);
    setCameraError("");
  };

  // Open the stream whenever the pop-up opens; always release the camera on close / unmount.
  useEffect(() => {
    if (!camera) return undefined;
    let cancelled = false;
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: camera === "video" })
      .then((stream) => {
        if (cancelled) return stream.getTracks().forEach((t) => t.stop());
        streamRef.current = stream;
        if (previewRef.current) previewRef.current.srcObject = stream;
      })
      .catch(() => setCameraError("Could not open the camera. Allow camera access in the browser, or use \"Upload from device\" instead."));
    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [camera]); // eslint-disable-line react-hooks/exhaustive-deps

  const choose = (kind) => {
    setMenuOpen(false);
    if (kind === "upload") return pickerRef.current && pickerRef.current.click();
    const capture = kind === "photo" ? photoCaptureRef : videoCaptureRef;
    if (isTouchDevice() || !hasCameraApi()) return capture.current && capture.current.click();
    setCameraError("");
    setCamera(kind);
  };

  const takePhoto = () => {
    const video = previewRef.current;
    if (!video || !video.videoWidth) return onError("The camera is not ready yet.");
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return onError("Could not capture the photo.");
        closeCamera();
        deliver(new File([blob], `photo-${Date.now()}.jpg`, { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.92,
    );
  };

  const startRecording = () => {
    if (!streamRef.current || typeof window.MediaRecorder === "undefined") {
      return setCameraError("This browser can't record video. Use \"Upload from device\" instead.");
    }
    const mime = ["video/webm;codecs=vp9,opus", "video/webm", "video/mp4"].find((m) => window.MediaRecorder.isTypeSupported(m));
    const recorder = new window.MediaRecorder(streamRef.current, { ...(mime ? { mimeType: mime } : {}), videoBitsPerSecond: 2000000 });
    chunksRef.current = [];
    recorder.ondataavailable = (e) => e.data && e.data.size && chunksRef.current.push(e.data);
    recorder.onstop = () => {
      const type = (recorder.mimeType || mime || "video/webm").split(";")[0];
      const blob = new Blob(chunksRef.current, { type });
      closeCamera();
      deliver(new File([blob], `video-${Date.now()}.${type === "video/mp4" ? "mp4" : "webm"}`, { type }));
    };
    recorderRef.current = recorder;
    recorder.start();
    setRecording(true);
    setSeconds(0);
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s + 1 >= MAX_RECORD_SECONDS) stopRecording();
        return s + 1;
      });
    }, 1000);
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
  };

  const menuItem = "flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-gray-700 hover:bg-gray-50 touch-manipulation";

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setMenuOpen((o) => !o)}
        disabled={uploading || disabled}
        className="border border-gray-200 p-2 rounded-lg touch-manipulation disabled:opacity-50"
        aria-label={`${hasMedia ? "Replace" : "Add"} photo or video for ${itemName}`}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        title={hasMedia ? "Replace photo / video" : "Add photo / video"}
      >
        {uploading ? <Loader2 size={13} className="text-blue-600 animate-spin" /> : <Upload size={13} className="text-gray-400" />}
      </button>

      {menuOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
          <div role="menu" className="absolute right-0 bottom-full mb-1 z-50 w-48 overflow-hidden rounded-xl border border-gray-100 bg-white py-1 shadow-lg">
            <button type="button" role="menuitem" className={menuItem} onClick={() => choose("photo")}>
              <Camera size={14} className="text-gray-400" /> Take photo
            </button>
            <button type="button" role="menuitem" className={menuItem} onClick={() => choose("video")}>
              <Video size={14} className="text-gray-400" /> Record video
            </button>
            <button type="button" role="menuitem" className={menuItem} onClick={() => choose("upload")}>
              <Upload size={14} className="text-gray-400" /> Upload from device
            </button>
          </div>
        </>
      )}

      <input ref={pickerRef} type="file" className="hidden" accept={[...IMAGE_TYPES, ...VIDEO_TYPES].join(",")} onChange={onInputChange} />
      <input ref={photoCaptureRef} type="file" className="hidden" accept="image/*" capture="environment" onChange={onInputChange} />
      <input ref={videoCaptureRef} type="file" className="hidden" accept="video/*" capture="environment" onChange={onInputChange} />

      {camera && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={closeCamera}>
          <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={closeCamera} aria-label="Close camera" className="absolute top-2 right-2 z-10 rounded-full bg-white/90 p-1.5 shadow touch-manipulation">
              <X size={16} className="text-gray-600" />
            </button>
            {cameraError ? (
              <p className="p-6 text-sm text-red-500">{cameraError}</p>
            ) : (
              <video ref={previewRef} autoPlay playsInline muted className="aspect-video w-full bg-black object-cover" />
            )}
            <div className="flex items-center justify-between gap-2 border-t border-gray-100 p-3">
              <p className="min-w-0 truncate text-sm font-semibold text-gray-900">
                {recording ? `Recording… ${seconds}s / ${MAX_RECORD_SECONDS}s` : camera === "photo" ? "Take a photo" : "Record a video"} · {itemName}
              </p>
              {!cameraError && camera === "photo" && (
                <button type="button" onClick={takePhoto} className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white touch-manipulation">
                  <Camera size={13} /> Capture
                </button>
              )}
              {!cameraError && camera === "video" && !recording && (
                <button type="button" onClick={startRecording} className="flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white touch-manipulation">
                  <Video size={13} /> Start
                </button>
              )}
              {!cameraError && camera === "video" && recording && (
                <button type="button" onClick={stopRecording} className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white touch-manipulation">
                  Stop &amp; use
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
